import customTheme from '@/constants/theme.custom';
import { Box, Text } from '@cmpsr/components';
import { HeroVideo } from '@/components/hero-video/hero-video';
import { useEffect, useState } from 'react';
import { GreenCheckmark } from '../../common/GreenCheckmark';

interface Feature {
  title: string;
  description: string;
}

interface DarkFeaturesSectionProps {
  onContentHeightChange?: (shouldScroll: boolean) => void;
}

const DarkFeaturesSection = ({ onContentHeightChange }: DarkFeaturesSectionProps) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const [isNearlyFull, setIsNearlyFull] = useState(false);

  useEffect(() => {
    const checkContentHeight = () => {
      const contentElement = document.getElementById('dark-features-content');
      if (contentElement && window.innerWidth >= 768) { // md breakpoint
        const contentHeight = contentElement.scrollHeight;
        const viewportHeight = window.innerHeight;
        const difference = viewportHeight - contentHeight;
        // Consider it "should scroll" if content exceeds viewport
        const newShouldScroll = contentHeight > viewportHeight;
        // Check if content nearly fills viewport (within 100px)
        const nearlyFull = difference >= 0 && difference < 100;
        setShouldScroll(newShouldScroll);
        setIsNearlyFull(nearlyFull);
        onContentHeightChange?.(newShouldScroll);
      } else {
        setShouldScroll(false);
        setIsNearlyFull(false);
        onContentHeightChange?.(false);
      }
    };

    checkContentHeight();
    window.addEventListener('resize', checkContentHeight);
    // Check again after a short delay to account for font loading
    setTimeout(checkContentHeight, 100);
    return () => window.removeEventListener('resize', checkContentHeight);
  }, [onContentHeightChange]);
  const features: Feature[] = [
    {
      title: "Learn your cancer risk for 21 major cancer types",
      description: "Know your risk for every major cancer based on over 500 validated risk factors"
    },
    {
      title: "Receive custom screening and testing protocols based on your risk",
      description: "Cancer is curable when it's caught early. Get screened for the right cancers at the right time for you."
    },
    {
      title: "Access a personalized action plan to reduce your risk up to 60%",
      description: "Learn simple steps and targeted lifestyle interventions personalized to your risk"
    },
    {
      title: "Track your risk trend over time",
      description: "Update your Action Plan and Testing Plan and watch your risk trend change over time"
    }
  ];

  return (
    <Box
      position="relative"
      width="100%"
      height={{ base: "auto", md: shouldScroll ? "auto" : "100%" }}
      minHeight={{ base: "850px", md: shouldScroll ? "100%" : "100%" }}
      bg="black"
      overflow="hidden"
    >
      <HeroVideo src="/videos/twilight-sky-2020-2.mp4">
          {/* Content */}
          <Box
            id="dark-features-content"
            width="100%"
            height={{ base: "100%", md: shouldScroll ? "auto" : "100%" }}
            display="flex"
            alignItems={{ base: "flex-start", md: "flex-start" }}
            justifyContent="center"
            overflowY={{ base: "visible", md: "hidden" }}
            px={{ base: "20px", sm: "40px", md: "40px", lg: "60px", xl: "80px" }}
            pt={{ base: "40px", sm: "50px", md: shouldScroll ? "80px" : isNearlyFull ? "80px" : "120px", lg: shouldScroll ? "80px" : isNearlyFull ? "80px" : "150px", xl: shouldScroll ? "80px" : isNearlyFull ? "80px" : "180px"}}
            pb={{ base: "30px", sm: "50px", md: shouldScroll ? "80px" : isNearlyFull ? "40px" : "40px", lg: shouldScroll ? "100px" : isNearlyFull ? "40px" : "40px" }}
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            <Box
              width="100%"
              maxWidth={{ base: "100%", md: "100%", lg: "600px", xl: "700px" }}
              mx="auto"
              display="flex"
              flexDirection="column"
              gap={{ base: "30px", sm: "35px", md: "25px", lg: "30px", xl: "35px" }}
              alignItems="flex-start"
            >
        {features.map((feature, index) => (
          <Box key={index} display="flex" gap={{ base: "16px", sm: "20px", md: "24px" }} alignItems="flex-start">
            <Box
              width={{ base: "18px", md: "24px" }}
              height={{ base: "18px", md: "24px" }}
              flexShrink={0}
              mt={{ base: "3px", md: "2px" }}
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
            >
              <GreenCheckmark />
            </Box>
            <Box display="flex" flexDirection="column" gap={{ base: "10px", sm: "12px", md: "10px", lg: "12px" }}>
              <Text
                fontSize={{ base: "18px", sm: "20px", md: "20px", lg: "22px", xl: "24px" }}
                fontWeight="bold"
                color="white"
                fontFamily={customTheme.typography.fonts.inter}
                lineHeight="1.3"
                textAlign="left"
              >
                {feature.title}
              </Text>
              <Text
                fontSize={{ base: "16px", sm: "18px", md: "18px", lg: "20px" }}
                color="rgba(255, 255, 255, 0.8)"
                fontFamily={customTheme.typography.fonts.inter}
                lineHeight="1.3"
                textAlign="left"
              >
                {feature.description}
              </Text>
            </Box>
          </Box>
        ))}
            </Box>
          </Box>
        </HeroVideo>
    </Box>
  );
};

export default DarkFeaturesSection;
